import type { HTMLProps, RefCallback } from 'react';
import React from 'react';
import type { ReactSliderProps } from 'react-slider';

import {
  MarkLabel,
  MarkLine,
  StyledReactSlider,
  Thumb,
  Track,
  TrackWrapper,
} from './PipsSlider.style';

interface HTMLPropsWithRefCallback<T> extends HTMLProps<T> {
  ref: RefCallback<T>;
}

type RenderThumb = (
  props: HTMLPropsWithRefCallback<HTMLDivElement>,
  state: { index: number; value: number; valueNow: number },
) => JSX.Element;

const renderThumb: RenderThumb = (props) => {
  const { key, ...others } = props;
  return (
    <Thumb
      key={`thumb-${key}`}
      {...others}
      as='div'
      data-test-id='pipsSliderThumb'
    ></Thumb>
  );
};

const renderTrack: ReactSliderProps<number>['renderTrack'] = (props) => {
  const { key, ...others } = props;
  return (
    <TrackWrapper
      key={key}
      {...others}
      as='div'
      data-test-id='pipsSliderTrackWrapper'
    >
      <Track data-test-id='pipsSliderTrack' />
    </TrackWrapper>
  );
};

export type pipsSliderProps = ReactSliderProps<number> & {
  className?: string;
  markLabels?: Record<number, string>;
};

export const PipsSlider = ({
  className,
  markLabels,
  value,
  ...props
}: pipsSliderProps) => {
  const renderMark = (
    props: HTMLPropsWithRefCallback<HTMLSpanElement>,
    markLabels?: Record<number, string>,
    firstOptionValue?: number | string,
    lastOptionValue?: number | string,
  ) => {
    const { key, ...others } = props;
    const label = markLabels ? markLabels[key as number] : key;
    const isActive = (value ?? 0) >= (key as number);

    const isLastOption = key === lastOptionValue;
    const isFirstOption = key === firstOptionValue;
    return (
      <span
        key={key}
        {...others}
        className={[
          'pips-mark',
          `${className}-mark`,
          isFirstOption && 'first',
          isLastOption && 'last',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <MarkLine $isActive={isActive} className={`${className}-line`} />
        <MarkLabel
          $isActive={isActive}
          className={[
            `${className}-label`,
            isFirstOption && 'first',
            isLastOption && 'last',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {label ?? key}
        </MarkLabel>
      </span>
    );
  };
  const firstOptionValue = props.min;
  const lastOptionValue = props.max;
  return (
    <StyledReactSlider
      className={className}
      data-test-id='pipsSlider'
      markClassName='pips-mark'
      renderMark={(props) =>
        renderMark(props, markLabels, firstOptionValue, lastOptionValue)
      }
      renderThumb={(props, state) => renderThumb(props, state)}
      renderTrack={renderTrack}
      trackClassName='pips-track'
      value={value}
      {...props}
    />
  );
};
